import { useTranslation } from "react-i18next";
import { InfoIcon } from "../../../../components/icons"
import { createTypographyHelper } from "../../../../utils/typography";
import { MotionModal, ModalCloseButton } from "../../../../components/motion";

const CancelBookingConfirmationModal = ({
    show = false,
    onClose = () => { },
    onConfirm = () => { },
    isLoading = true,
    refundAmount = 0,
    cancellationFee = 0,
    cancellationPercent = 0,
    desc = {
        en: "",
        jp: ""
    },
    meetAssistCancellationFee = 0,
    meetAssistCancellationPercent = 0,
    meetAssistCancellationDesc = {
        en: "",
        jp: ""
    },
    paymentStatus,
    deferredPayment,
    meetAssist,
    role = "individual",
    totalPrice = 0,
}) => {
    const { t, i18n } = useTranslation();
    const getTypographyClass = createTypographyHelper(i18n.language);

    const isUsingMeetAssist = (meetAssist, ind = 0) => {
        return meetAssist[ind].meetAssistPrice > 0;
    }

    const getCancellationPolicyLink = (role) => {
        if (role === "business") {
            return "https://theluxuryfleet.com/corporate-cancellation-policy";
        } else {
            if (i18n.language === "jp") {
                return "https://theluxuryfleet.com/jp/cancellation-policy";
            } else {
                return "https://theluxuryfleet.com/en/cancellation-policy";
            }
        }
    }

    const isFullCancellationFee = (cancellationPercent, meetAssistCancellationPercent) => {
        if (cancellationPercent === 100) {
            if (isUsingMeetAssist(meetAssist, 0)) {
                if (meetAssistCancellationPercent.arrival !== 100) {
                    return false;
                }
            }

            if (isUsingMeetAssist(meetAssist, 1)) {
                if (meetAssistCancellationPercent.departure !== 100) {
                    return false;
                }
            }

            return true;
        } else {
            return false;
        }
    }

    const getRefundAmountDetail = () => {
        let details = `${totalPrice.toLocaleString()}${t("bookingDetail.cancelBookingConfirmationModal.jpy")} - ${cancellationFee?.toLocaleString()}${t("bookingDetail.cancelBookingConfirmationModal.jpy")}`;

        if(isUsingMeetAssist(meetAssist, 0)) {
            details += ` - ${meetAssistCancellationFee?.arrival.toLocaleString()}${t("bookingDetail.cancelBookingConfirmationModal.jpy")}`;
        }

        if(isUsingMeetAssist(meetAssist, 1)) {
            details += ` - ${meetAssistCancellationFee?.departure.toLocaleString()}${t("bookingDetail.cancelBookingConfirmationModal.jpy")}`;
        }

        return details;
    }

    return (
        <MotionModal
            show={show}
            onClose={onClose}
            maxWidthClassName="tw:max-w-[660px]"
            panelClassName="tw:px-4 tw:py-5 tw:md:px-10 tw:md:py-8"
        >
                <ModalCloseButton onClick={onClose} />

                {
                    isLoading ? (
                        /* --- LOADING STATE --- */
                        <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:mt-6">
                            {/* Simple CSS Spinner */}
                            <div className="tw:w-12 tw:h-12 tw:border-4 tw:border-[#f3f3f3] tw:border-t-[#7c7b7b] tw:rounded-full tw:animate-spin tw:mb-6"></div>

                            <p className="tw:!text-[#7C7B7B] tw:!text-[15px] tw:!font-[400] tw:!italic tw:leading-relaxed tw:!mb-0">
                                {t("bookingDetail.cancelBookingConfirmationModal.loading")}
                            </p>
                        </div>
                    ) : (
                        <div className="tw:flex tw:flex-col tw:items-center tw:text-center tw:mt-6 tw:md:mt-4 tw:mb-2">
                            <div className="tw:text-[#282828] tw:text-[18px] tw:font-[600] tw:leading-[25px] tw:font-semibold tw:font-hanken-grotesk tw:mb-2 tw:max-w-[560px]">
                                {t("bookingDetail.cancelBookingConfirmationModal.title")}
                            </div>

                            {
                                (paymentStatus === "CHARGE_PENDING" && deferredPayment) && (
                                    <div className="tw:!grid tw:!w-full tw:!mb-4">
                                        <p className="tw:!text-[#666666] tw:!text-[15px] tw:!font-[400] tw:!mb-0">
                                            {t("bookingDetail.cancelBookingConfirmationModal.notIncludedToMonthlyTotal")}
                                        </p>
                                    </div>
                                )
                            }

                            {
                                (paymentStatus === "CHARGE_PENDING" && !deferredPayment) && (
                                    <div className="tw:!grid tw:!w-full tw:!mb-4">
                                        <p className="tw:!text-[#666666] tw:!text-[15px] tw:!font-[400] tw:!mb-0">
                                            {t("bookingDetail.cancelBookingConfirmationModal.noCharge")}
                                        </p>
                                    </div>
                                )
                            }

                            {
                                (isFullCancellationFee(cancellationPercent, meetAssistCancellationPercent) && !deferredPayment) && (
                                    <div className="tw:!grid tw:!w-full tw:!mb-4">
                                        <p className="tw:!text-[#666666] tw:!text-[15px] tw:!font-[400] tw:!mb-0">
                                            {t("bookingDetail.cancelBookingConfirmationModal.fullCancellationFee")}
                                        </p>
                                    </div>
                                )
                            }

                            {
                                paymentStatus !== "CHARGE_PENDING" && (
                                    !deferredPayment ? (
                                        <div className="tw:!bg-[#f9f9f9] tw:!rounded-[8px] tw:!p-6 tw:!mb-8 tw:!w-full">
                                            {
                                                refundAmount !== 0 && (
                                                    <div className="tw:!mb-4">
                                                        <span className="tw:!text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!items-center tw:!justify-start tw:!text-left">{t("bookingDetail.cancelBookingConfirmationModal.refundAmount")}&nbsp;<p className="tw:!text-[#7C7B7B] tw:!font-[400] tw:!text-[13px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t("bookingDetail.cancelBookingConfirmationModal.refundAmountNote")}</p></span>
                                                        <p className="tw:!text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{refundAmount.toLocaleString()}{t("bookingDetail.cancelBookingConfirmationModal.jpy")}&nbsp;<p className="tw:!text-[#7C7B7B] tw:!font-[400] tw:!text-[13px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{`(= ${getRefundAmountDetail()})`}</p></p>
                                                    </div>
                                                )
                                            }

                                            <div className={`${refundAmount > 0 ? "tw:border-t tw:border-[#e0e0e0] tw:pt-4" : ""} tw:flex tw:gap-4 tw:!mb-4`}>
                                                <div className="tw:flex-1">
                                                    <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t("bookingDetail.cancelBookingConfirmationModal.cancellationRate")}</p>
                                                    <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">
                                                        {i18n.language === "en" ? desc.en : desc.jp}
                                                    </p>
                                                </div>
                                                <div className="tw:border-l tw:border-[#e0e0e0] tw:pl-4 tw:flex-1">
                                                    <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t("bookingDetail.cancelBookingConfirmationModal.cancellationFee")}</p>
                                                    <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{cancellationFee.toLocaleString()}{t("bookingDetail.cancelBookingConfirmationModal.jpy")}</p>
                                                </div>
                                            </div>

                                            {isUsingMeetAssist(meetAssist, 0) && (
                                                <div className="tw:border-t tw:border-[#e0e0e0] tw:pt-4 tw:flex tw:gap-4 tw:!mb-4">
                                                    <div className="tw:flex-1">
                                                        <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t(`bookingDetail.cancelBookingConfirmationModal.${meetAssistCancellationDesc?.arrival?.type === "vipMeetAssist" ? "arrivalVIPMeetAssistCancellationRate" : "arrivalMeetAssistCancellationRate"}`)}</p>
                                                        <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">
                                                            {i18n.language === "en" ? meetAssistCancellationDesc?.arrival?.en : meetAssistCancellationDesc?.arrival?.jp}
                                                        </p>
                                                    </div>
                                                    <div className="tw:border-l tw:border-[#e0e0e0] tw:pl-4 tw:flex-1">
                                                        <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t(`bookingDetail.cancelBookingConfirmationModal.${meetAssistCancellationDesc?.arrival?.type === "vipMeetAssist" ? "arrivalVIPMeetAssistCancellationFee" : "arrivalMeetAssistCancellationFee"}`)}</p>
                                                        <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{meetAssistCancellationFee?.arrival?.toLocaleString()}{t("bookingDetail.cancelBookingConfirmationModal.jpy")}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {isUsingMeetAssist(meetAssist, 1) && (
                                                <div className="tw:border-t tw:border-[#e0e0e0] tw:pt-4 tw:flex tw:gap-4 tw:!mb-4">
                                                    <div className="tw:flex-1">
                                                        <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t(`bookingDetail.cancelBookingConfirmationModal.${meetAssistCancellationDesc?.departure?.type === "vipMeetAssist" ? "departureVIPMeetAssistCancellationRate" : "departureMeetAssistCancellationRate"}`)}</p>
                                                        <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">
                                                            {i18n.language === "en" ? meetAssistCancellationDesc?.departure?.en : meetAssistCancellationDesc?.departure?.jp}
                                                        </p>
                                                    </div>
                                                    <div className="tw:border-l tw:border-[#e0e0e0] tw:pl-4 tw:flex-1">
                                                        <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t(`bookingDetail.cancelBookingConfirmationModal.${meetAssistCancellationDesc?.departure?.type === "vipMeetAssist" ? "departureVIPMeetAssistCancellationFee" : "departureMeetAssistCancellationFee"}`)}</p>
                                                        <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{meetAssistCancellationFee?.departure?.toLocaleString()}{t("bookingDetail.cancelBookingConfirmationModal.jpy")}</p>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="tw:border-t tw:border-[#e0e0e0] tw:pt-4 tw:flex tw:gap-4">
                                                <a
                                                    href={getCancellationPolicyLink(role)}
                                                    target="_blank"
                                                    className="tw:!underline tw:!decoration-[#7C7B7B] tw:inline-flex tw:items-center tw:gap-2"
                                                >
                                                    <InfoIcon
                                                        color="#7C7B7B"
                                                        size={14}
                                                        className="tw:shrink-0"
                                                    />
                                                    <span className="tw:text-[#7C7B7B] tw:!font-[400] tw:!text-[13px]">
                                                        {t("bookingDetail.cancelBookingConfirmationModal.cancellationPolicy")}
                                                    </span>
                                                </a>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="tw:!bg-[#f9f9f9] tw:!rounded-[8px] tw:!p-6 tw:!mb-8 tw:!w-full tw:mt-3">
                                            {
                                                (cancellationPercent === 0 && meetAssistCancellationPercent === 0) ? (
                                                    <div className="tw:!mb-4">
                                                        <p className="tw:!text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t("bookingDetail.cancelBookingConfirmationModal.cancellationRate")}</p>
                                                        <p className="tw:!text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{i18n.language === "en" ? desc.en : desc.jp}</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="tw:flex tw:gap-4 tw:!mb-4">
                                                            <div className="tw:flex-1">
                                                                <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t("bookingDetail.cancelBookingConfirmationModal.cancellationRate")}</p>
                                                                <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">
                                                                    {i18n.language === "en" ? desc.en : desc.jp}
                                                                </p>
                                                            </div>
                                                            <div className="tw:border-l tw:border-[#e0e0e0] tw:pl-4 tw:flex-1">
                                                                <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t("bookingDetail.cancelBookingConfirmationModal.cancellationFee")}</p>
                                                                <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{cancellationFee.toLocaleString()}{t("bookingDetail.cancelBookingConfirmationModal.jpy")}</p>
                                                            </div>
                                                        </div>
                                                        {isUsingMeetAssist(meetAssist, 0) && (
                                                            <div className="tw:border-t tw:border-[#e0e0e0] tw:pt-4 tw:flex tw:gap-4 tw:!mb-4">
                                                                <div className="tw:flex-1">
                                                                    <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t(`bookingDetail.cancelBookingConfirmationModal.${meetAssistCancellationDesc?.arrival?.type === "vipMeetAssist" ? "arrivalVIPMeetAssistCancellationRate" : "arrivalMeetAssistCancellationRate"}`)}</p>
                                                                    <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">
                                                                        {i18n.language === "en" ? meetAssistCancellationDesc?.arrival?.en : meetAssistCancellationDesc?.arrival?.jp}
                                                                    </p>
                                                                </div>
                                                                <div className="tw:border-l tw:border-[#e0e0e0] tw:pl-4 tw:flex-1">
                                                                    <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t(`bookingDetail.cancelBookingConfirmationModal.${meetAssistCancellationDesc?.arrival?.type === "vipMeetAssist" ? "arrivalVIPMeetAssistCancellationFee" : "arrivalMeetAssistCancellationFee"}`)}</p>
                                                                    <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{meetAssistCancellationFee?.arrival?.toLocaleString()}{t("bookingDetail.cancelBookingConfirmationModal.jpy")}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {isUsingMeetAssist(meetAssist, 1) && (
                                                            <div className="tw:border-t tw:border-[#e0e0e0] tw:pt-4 tw:flex tw:gap-4 tw:!mb-4">
                                                                <div className="tw:flex-1">
                                                                    <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t(`bookingDetail.cancelBookingConfirmationModal.${meetAssistCancellationDesc?.departure?.type === "vipMeetAssist" ? "departureVIPMeetAssistCancellationRate" : "departureMeetAssistCancellationRate"}`)}</p>
                                                                    <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">
                                                                        {i18n.language === "en" ? meetAssistCancellationDesc?.departure?.en : meetAssistCancellationDesc?.departure?.jp}
                                                                    </p>
                                                                </div>
                                                                <div className="tw:border-l tw:border-[#e0e0e0] tw:pl-4 tw:flex-1">
                                                                    <p className="tw:text-[#282828] tw:!font-[700] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{t(`bookingDetail.cancelBookingConfirmationModal.${meetAssistCancellationDesc?.departure?.type === "vipMeetAssist" ? "departureVIPMeetAssistCancellationFee" : "departureMeetAssistCancellationFee"}`)}</p>
                                                                    <p className="tw:text-[#282828] tw:!font-[400] tw:!text-[14px] tw:!mb-0 tw:!flex tw:!justify-start tw:!text-left">{meetAssistCancellationFee?.departure?.toLocaleString()}{t("bookingDetail.cancelBookingConfirmationModal.jpy")}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )
                                            }

                                            <div className="tw:border-t tw:border-[#e0e0e0] tw:pt-4 tw:flex tw:gap-4">
                                                <a
                                                    href={getCancellationPolicyLink(role)}
                                                    target="_blank"
                                                    className="tw:!underline tw:!decoration-[#7C7B7B] tw:inline-flex tw:items-center tw:gap-2"
                                                >
                                                    <InfoIcon
                                                        color="#7C7B7B"
                                                        size={14}
                                                        className="tw:shrink-0"
                                                    />
                                                    <span className="tw:text-[#7C7B7B] tw:!font-[400] tw:!text-[13px]">
                                                        {t("bookingDetail.cancelBookingConfirmationModal.cancellationPolicy")}
                                                    </span>
                                                </a>
                                            </div>
                                        </div>
                                    )
                                )
                            }

                            {/* Action Buttons */}
                            <div className="tw:!grid tw:!grid-cols-2 tw:!gap-4 tw:!w-full">
                                <button
                                    onClick={onClose}
                                    className="tw:!border tw:!border-[#ccc] tw:!rounded-[8px] tw:!py-3 tw:!font-bold tw:!text-[#282828] tw:hover:!bg-gray-50 tw:!transition-colors tw:!w-full"
                                >
                                    <span className={getTypographyClass(14)}>{t("bookingDetail.cancelBookingConfirmationModal.close")}</span>
                                </button>
                                <button
                                    onClick={onConfirm}
                                    className="tw:!bg-[#f34336] tw:!rounded-[8px] tw:!py-3 tw:!font-bold tw:!text-white tw:hover:!bg-[#d32f2f] tw:!transition-colors tw:!w-full"
                                >
                                    <span className={getTypographyClass(14)}>{t("bookingDetail.cancelBookingConfirmationModal.cancelBooking")}</span>
                                </button>
                            </div>

                        </div>
                    )
                }
        </MotionModal>
    );
};

export default CancelBookingConfirmationModal;